import { DEFAULT_LABEL_STYLE, OverlayStyle } from '../store/types';
import { getLabelBackground, neonColor } from '../components/labels/shared/labelBadge';

// Fill opacity added while highlighted
const HIGHLIGHT_OPACITY_STEP = 0.1;

export type OverlayPoint = { x: number; y: number };

/**
  * Area-weighted centroid of a polygon. Falls back to the average of its
  * vertices for degenerate (zero-area) polygons.
  */
export function polygonCentroid(points: OverlayPoint[]): OverlayPoint {
  if (points.length === 0) return { x: 0, y: 0 };

  let doubleArea = 0;
  let x = 0;
  let y = 0;
  points.forEach((start, index) => {
    const end = points[(index + 1) % points.length];
    const cross = (start.x * end.y) - (end.x * start.y);
    doubleArea += cross;
    x += (start.x + end.x) * cross;
    y += (start.y + end.y) * cross;
  });

  if (Math.abs(doubleArea) < 1e-12) {
    return {
      x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
      y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
    };
  }

  return { x: x / (3 * doubleArea), y: y / (3 * doubleArea) };
}

// Border colour of an overlay: its neon colour (the border colour is then the
// label one), or its border colour, or its text colour
function overlayBorderColor(style: OverlayStyle) {
  if (style.variant === 'neon') return neonColor(style);
  if (style.borderColor && style.borderColor !== 'transparent') return style.borderColor;
  return style.color ?? DEFAULT_LABEL_STYLE.color;
}

/**
  * Polygon look of an overlay, derived from its label style: filled exactly
  * like the label background (a light tint of the glow colour on neon), and
  * bordered with its border colour.
  */
export function overlayShapeStyle(style: OverlayStyle) {
  const isNeon = style.variant === 'neon';
  const background = getLabelBackground(style);

  return {
    isNeon,
    fillColor: background.color,
    fillOpacity: background.opacity,
    highlightedFillOpacity: Math.min(1, Math.round((background.opacity + HIGHLIGHT_OPACITY_STEP) * 100) / 100),
    strokeColor: overlayBorderColor(style),
    glowColor: neonColor(style),
    strokeWidth: isNeon ? 2.5 : Math.max(2, (style.fontSize ?? DEFAULT_LABEL_STYLE.fontSize) * 0.09),
  };
}
